import React, { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  MessagesSquare,
  CheckCircle2,
  Clock,
  Activity,
  ArrowRight,
  Smile,
  Phone,
  MessageSquare,
  MessagesSquare as ChatIcon,
  Mail,
} from 'lucide-react';
import { Card, PageTitle } from './ui';
import { logsAPI, logChannel, logStatus, timeAgo, formatDuration } from './api';
import { LoadingRows, ErrorNote } from './DemoCustomerAIPages';

/* ---------- charts (data-driven) ---------- */

const niceMax = (v) => {
  if (v <= 5) return 5;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  return Math.ceil(v / pow) * pow;
};

const LineChart = ({ series }) => {
  const W = 560;
  const H = 200;
  const PAD_L = 40;
  const PAD_B = 26;
  if (!series || series.length === 0) {
    return <p className="py-16 text-center text-[13px] text-slate-400">No conversations in this period yet.</p>;
  }
  const max = niceMax(Math.max(...series.map((s) => s.v), 1));
  const peakIdx = series.reduce((best, s, i) => (s.v > series[best].v ? i : best), 0);
  const xFor = (i) => PAD_L + (series.length === 1 ? 0.5 : i / (series.length - 1)) * (W - PAD_L - 14);
  const pts = series.map((s, i) => ({ x: xFor(i), y: 10 + (1 - s.v / max) * (H - PAD_B - 10) }));
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const area = `${path} L${pts[pts.length - 1].x},${H - PAD_B} L${pts[0].x},${H - PAD_B} Z`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <defs>
        <linearGradient id="tealFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0D9488" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#0D9488" stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((v) => {
        const y = 10 + (1 - v / max) * (H - PAD_B - 10);
        return (
          <g key={v}>
            <line x1={PAD_L} y1={y} x2={W - 14} y2={y} stroke="#F1F5F9" strokeWidth="1" />
            <text x={PAD_L - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill="#94A3B8">
              {v >= 1000 ? `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}K` : v}
            </text>
          </g>
        );
      })}
      <path d={area} fill="url(#tealFade)" />
      <path d={path} fill="none" stroke="#0D9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={i === peakIdx ? 4.5 : 3.5} fill="#fff" stroke="#0D9488" strokeWidth="2" />
      ))}
      {series.length > 0 && (
        <g transform={`translate(${Math.min(Math.max(pts[peakIdx].x - 36, 0), W - 72)},${Math.max(pts[peakIdx].y - 42, 0)})`}>
          <rect width="72" height="30" rx="6" fill="#0F172A" />
          <text x="36" y="13" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff">
            {series[peakIdx].v.toLocaleString()}
          </text>
          <text x="36" y="24" textAnchor="middle" fontSize="8.5" fill="#CBD5E1">
            {series[peakIdx].day}
          </text>
        </g>
      )}
      {series.map((s, i) => (
        // Only label a handful of x ticks to avoid crowding
        (series.length <= 8 || i % Math.ceil(series.length / 7) === 0) && (
          <text key={s.day} x={pts[i].x} y={H - 8} textAnchor="middle" fontSize="10" fill="#94A3B8">
            {s.day}
          </text>
        )
      ))}
    </svg>
  );
};

const Donut = ({ segments }) => {
  const R = 60;
  const C = 2 * Math.PI * R;
  let offset = 25; // start at top
  const total = segments.reduce((a, s) => a + s.pct, 0);
  if (total === 0) {
    return (
      <svg viewBox="0 0 160 160" className="h-[170px] w-[170px]">
        <circle cx="80" cy="80" r={R} fill="none" stroke="#F1F5F9" strokeWidth="26" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 160 160" className="h-[170px] w-[170px]">
      {segments.map(({ name, pct, color }) => {
        const dash = (pct / 100) * C;
        const el = (
          <circle
            key={name}
            cx="80"
            cy="80"
            r={R}
            fill="none"
            stroke={color}
            strokeWidth="26"
            strokeDasharray={`${Math.max(dash - 1.5, 0)} ${C - dash + 1.5}`}
            strokeDashoffset={(offset / 100) * C}
          />
        );
        offset -= pct;
        return el;
      })}
    </svg>
  );
};

const CardHeader = ({ title, action }) => (
  <div className="flex items-center justify-between px-5 pt-4">
    <h2 className="text-[15px] font-semibold">{title}</h2>
    {action}
  </div>
);

const CHANNEL_META = [
  { key: 'phone', name: 'Phone', color: '#0D9488', icon: Phone },
  { key: 'chat', name: 'Web Chat', color: '#7C3AED', icon: ChatIcon },
  { key: 'sms', name: 'SMS', color: '#F97316', icon: MessageSquare },
  { key: 'email', name: 'Email', color: '#64748B', icon: Mail },
];

const DemoInsights = () => {
  const [usage, setUsage] = useState(null);
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([logsAPI.usage(), logsAPI.list()])
      .then(([u, l]) => {
        setUsage(u.data.stats || {});
        setLogs(l.data.logs || []);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const oc = usage?.outcomeCounts || {};
  const mc = usage?.moodCounts || {};
  const analyzed = (oc.success || 0) + (oc.failure || 0);
  const resolutionRate = analyzed ? Math.round((oc.success / analyzed) * 1000) / 10 : 0;
  const moodTotal = (mc.positive || 0) + (mc.neutral || 0) + (mc.negative || 0);
  const satisfaction = moodTotal
    ? Math.round((((mc.positive || 0) * 5 + (mc.neutral || 0) * 3 + (mc.negative || 0) * 1) / moodTotal) * 10) / 10
    : 0;

  const kpis = useMemo(() => {
    if (!usage) return [];
    return [
      { label: 'Total Conversations', value: (usage.totalCalls || 0).toLocaleString(), sub: 'in the last 30 days', icon: MessagesSquare, tint: 'bg-teal-50 text-teal-600' },
      { label: 'Successful', value: (oc.success || 0).toLocaleString(), sub: `${analyzed.toLocaleString()} analyzed`, icon: CheckCircle2, tint: 'bg-emerald-50 text-emerald-600' },
      { label: 'Resolution Rate', value: `${resolutionRate}%`, sub: 'success of analyzed calls', icon: Activity, tint: 'bg-sky-50 text-sky-600' },
      { label: 'Avg Call Length', value: formatDuration(usage.averageDuration || 0), sub: 'per conversation', icon: Clock, tint: 'bg-orange-50 text-orange-500' },
    ];
  }, [usage, oc.success, analyzed, resolutionRate]);

  const series = useMemo(() => {
    const daily = usage?.dailyCalls || {};
    return Object.entries(daily)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .slice(-14)
      .map(([date, v]) => ({ day: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), v }));
  }, [usage]);

  const channels = useMemo(() => {
    if (!logs) return [];
    const counts = { phone: 0, sms: 0, chat: 0, email: 0 };
    logs.forEach((l) => {
      counts[logChannel(l)] += 1;
    });
    const total = logs.length || 1;
    return CHANNEL_META.map((c) => ({ ...c, count: counts[c.key], pct: Math.round((counts[c.key] / total) * 1000) / 10 }));
  }, [logs]);

  const topAgents = useMemo(() => {
    const counts = usage?.scriptCounts || {};
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  }, [usage]);

  const outcomes = [
    { name: 'Successful', v: oc.success || 0, color: '#16A34A' },
    { name: 'Failed', v: oc.failure || 0, color: '#EF4444' },
    { name: 'Unclassified', v: oc.unknown || 0, color: '#94A3B8' },
  ];
  const outcomeTotal = outcomes.reduce((a, o) => a + o.v, 0) || 1;

  const moods = [
    { name: 'Positive', v: mc.positive || 0, color: '#16A34A' },
    { name: 'Neutral', v: mc.neutral || 0, color: '#F59E0B' },
    { name: 'Negative', v: mc.negative || 0, color: '#EF4444' },
  ];

  const recent = useMemo(() => (logs || []).slice(0, 5), [logs]);

  return (
    <div className="h-full overflow-y-auto pb-4 pr-1">
      <PageTitle
        title="Insights"
        subtitle="Understand performance and improve your Customer AI experience."
        action={
          <span className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-medium text-slate-500 shadow-sm">
            <Calendar size={15} className="text-slate-400" /> Last 30 days
          </span>
        }
      />

      {error && <div className="mt-6"><ErrorNote error={error} /></div>}
      {!error && !usage && <LoadingRows label="Loading insights…" />}

      {!error && usage && (
        <>
          {/* KPIs */}
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {kpis.map(({ label, value, sub, icon: Icon, tint }) => (
              <Card key={label} className="p-5">
                <div className="flex items-center gap-3">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full ${tint}`}>
                    <Icon size={18} strokeWidth={1.9} />
                  </span>
                  <span className="text-[13px] font-medium text-slate-500">{label}</span>
                </div>
                <div className="mt-3 text-[26px] font-bold tracking-tight">{value}</div>
                <div className="mt-0.5 text-[12px] font-medium text-slate-400">{sub}</div>
              </Card>
            ))}
          </div>

          {/* Row 1 */}
          <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[1.7fr_1.2fr_1fr]">
            <Card className="pb-4">
              <CardHeader title="Conversations Over Time" />
              <div className="px-4 pt-3">
                <LineChart series={series} />
              </div>
            </Card>

            <Card className="pb-4">
              <CardHeader title="Conversations by Channel" />
              <div className="flex items-center gap-4 px-5 pt-3">
                <Donut segments={channels} />
                <div className="flex-1 space-y-2.5">
                  {channels.map(({ name, pct, color }) => (
                    <div key={name} className="flex items-center justify-between text-[12.5px]">
                      <span className="flex items-center gap-2 text-slate-600">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                        {name}
                      </span>
                      <span className="font-semibold">{pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <Card className="pb-2">
              <CardHeader title="Top AI Agents" />
              <div className="px-5 pt-2">
                {topAgents.length === 0 && (
                  <p className="py-8 text-center text-[12.5px] text-slate-400">No agent activity yet.</p>
                )}
                {topAgents.map(({ name, count }, i) => (
                  <div key={name} className="flex items-center gap-3 border-b border-slate-50 py-2.5 last:border-0">
                    <span className="w-3 text-[12.5px] font-semibold text-slate-400">{i + 1}</span>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-50 text-teal-600 text-[12px] font-bold">
                      {name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="flex-1 truncate text-[13px] font-semibold">{name}</span>
                    <span className="text-[13px] font-semibold text-slate-500">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Row 2 */}
          <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[1.7fr_1.2fr_1fr]">
            <Card className="pb-5">
              <CardHeader title="Conversations by Outcome" />
              <div className="space-y-3.5 px-5 pt-4">
                {outcomes.map(({ name, v, color }) => (
                  <div key={name} className="flex items-center gap-3">
                    <span className="w-[110px] shrink-0 text-[12.5px] text-slate-600">{name}</span>
                    <div className="h-3.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full" style={{ width: `${(v / outcomeTotal) * 100}%`, backgroundColor: color }} />
                    </div>
                    <span className="w-20 text-right text-[12.5px] font-semibold">
                      {v.toLocaleString()} · {Math.round((v / outcomeTotal) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="pb-4">
              <CardHeader title="Customer Sentiment" />
              <div className="flex items-center gap-5 px-5 pt-3">
                <div className="relative h-[130px] w-[130px] shrink-0">
                  <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#F1F5F9" strokeWidth="12" />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#16A34A"
                      strokeWidth="12"
                      strokeLinecap="round"
                      strokeDasharray={`${(satisfaction / 5) * 2 * Math.PI * 50} ${2 * Math.PI * 50}`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[26px] font-bold leading-none">{satisfaction || '—'}</span>
                    <span className="text-[11px] text-slate-400">/ 5.0</span>
                  </div>
                </div>
                <div className="flex-1 space-y-2.5">
                  {moods.map(({ name, v, color }) => (
                    <div key={name} className="flex items-center gap-2 text-[12px]">
                      <span className="w-14 font-semibold text-slate-500">{name}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${moodTotal ? (v / moodTotal) * 100 : 0}%`, backgroundColor: color }} />
                      </div>
                      <span className="w-9 text-right font-medium text-slate-500">
                        {moodTotal ? Math.round((v / moodTotal) * 100) : 0}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1.5 px-5 text-[11.5px] text-slate-400">
                <Smile size={13} /> Based on {moodTotal.toLocaleString()} analyzed conversations
              </div>
            </Card>

            <Card className="pb-2">
              <CardHeader title="Recent Activity" />
              <div className="px-5 pt-2">
                {recent.length === 0 && (
                  <p className="py-8 text-center text-[12.5px] text-slate-400">No recent conversations.</p>
                )}
                {recent.map((log) => {
                  const st = logStatus(log);
                  return (
                    <div key={log._id} className="flex items-start gap-3 border-b border-slate-50 py-3 last:border-0">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-500">
                        <MessageSquare size={14} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold">{log.scriptName || 'Conversation'}</p>
                        <p className="mt-0.5 truncate text-[12px] text-slate-500">{st.label}</p>
                      </div>
                      <span className="shrink-0 text-[11px] text-slate-400">{timeAgo(log.createdAt)}</span>
                    </div>
                  );
                })}
                {recent.length > 0 && (
                  <div className="flex justify-end py-2.5">
                    <a href="#/dashboard/logs" className="flex items-center gap-1.5 text-[12.5px] font-semibold text-teal-700 hover:text-teal-800">
                      View all logs <ArrowRight size={13} />
                    </a>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default DemoInsights;
